import { useState } from 'react';
import classes from './PriceTable.module.css';
import clsx from 'clsx';
import PricePlanTable from './PricePlanTable';
import Tabs, { Tab } from 'Components/Molecules/Tabs/Tabs';
import Icon from 'Components/Atoms/Icon/Icon';
import TextButton from 'Components/Molecules/TextButton';
import Collapse from 'Components/Atoms/Collapse/CollapseContent';
import ComparativeTable from './ComparativeTable';
import useProjectSelected from 'Hooks/useProjectSelected';
import CardEnterprise from './CardEnterprise';
import { useTranslation } from 'react-i18next';
import usePriceTableConfig from './usePriceTableConfig';
import SketchyArrow from './SketchyArrow';
import { motion } from 'framer-motion';

export default function PriceTable() {
    const [tab, setTab] = useState('monthly');
    const [showComparative, setShowComparative] = useState(false);
    const { project } = useProjectSelected();
    const { plans, table } = usePriceTableConfig();

    const { t } = useTranslation('pricing');

    return (
        <>
            <section className={classes.section}>
                <div className="text-center">
                    <h1 className={classes.title}>{t('title')}</h1>
                    <p className={classes.description}>{t('description')}</p>
                </div>

                <div className={classes.tabsContainer}>
                    <div className={classes.relative}>
                        <Tabs value={tab} onChange={setTab} className="h-14 text-base bg-black/5">
                            <Tab value="monthly" className="text-base px-2">
                                {t('monthly')}
                            </Tab>
                            <Tab value="yearly" className="text-base px-2">
                                {t('yearly')}
                            </Tab>
                        </Tabs>

                        <div className={classes.saving} style={{ color: tab === 'monthly' ? '#999' : '#000' }}>
                            <motion.span
                                className={classes.savingMessage}
                                animate={tab === 'monthly' ? { y: [0, -5, 0, -3, 0] } : { y: 0 }}
                                transition={
                                    tab === 'monthly'
                                        ? {
                                              y: {
                                                  duration: 1.5,
                                                  repeat: Infinity,
                                                  repeatDelay: 1,
                                                  ease: 'easeInOut',
                                              },
                                          }
                                        : { y: { duration: 0.3 } }
                                }
                            >
                                {t('months-free')}
                            </motion.span>
                            <SketchyArrow
                                className={classes.savingArrow}
                                style={{ opacity: tab === 'monthly' ? 0.4 : 1 }}
                            />
                        </div>
                    </div>
                </div>

                <div className={classes.grid}>
                    {plans.map((item) => (
                        <PricePlanTable
                            key={item.title}
                            {...item}
                            currentPlan={project?.subscription?.plan === item.id}
                            periodicity={tab}
                        />
                    ))}

                    <CardEnterprise />
                </div>
            </section>

            <section className={classes.section}>
                <div className="text-center py-3">
                    <TextButton className="inline-flex flex-col" onClick={() => setShowComparative(!showComparative)}>
                        <div>{showComparative ? t('hide') : t('compare-plans')}</div>
                        <div>
                            <Icon
                                name="chevron-down"
                                color="currentColor"
                                size={28}
                                className={clsx({ [classes.rotate]: showComparative })}
                            />
                        </div>
                    </TextButton>
                </div>
                <Collapse open={showComparative}>
                    <ComparativeTable
                        title={t('comparative-table.title')}
                        description={t('comparative-table.description')}
                        columns={table.columns}
                        rows={table.rows}
                        periodicity={tab}
                    />
                </Collapse>
            </section>
        </>
    );
}
